import { Controller } from "@hotwired/stimulus"
import YouTubePlayer from "youtube-player"

/**
 * Display and control YouTube videos.
 *
 * Initial code borrowed from @leastbad's stimulus-youtube controller.
 * https://gist.github.com/leastbad/33a97dc724d7bf21b7f6ff1992e5aab9
 *
 * Utilizes the "youtube-player" iframe API abstraction:
 * https://github.com/gajus/youtube-player
 *
 * Example:
 *
 *   <div
 *     class="youtube-video relative h-0 aspect-w-16 aspect-h-9"
 *     data-controller="ui--youtube-video"
 *     data-ui--youtube-video-code-value="sp3B97N67Cw"
 *     data-ui--youtube-video-width-value="640"
 *     data-ui--youtube-video-height-value="360"
 *   >
 *     <button data-action="ui--youtube-video#play">Play</button>
 *     <button data-action="ui--youtube-video#pause">Pause</button>
 *     <button data-action="ui--youtube-video#stop">Stop</button>
 *     <br />
 *     <div data-ui--youtube-video-target="frame"></div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["frame"]

  static values = {
    code: String,
    duration: Number,
    endingAt: Number,
    height: { type: Number, default: 360 },
    ready: { type: Boolean, default: false },
    startingAt: Number,
    state: Number,
    time: Number,
    width: { type: Number, default: 640 },
  }

  initialize() {
    this.element["youtube"] = this
  }

  connect() {
    if (!this.hasCodeValue) {
      console.log("No YouTube code value was provided.")
      return
    }

    const player = YouTubePlayer(this.frameTarget, {
      height: this.heightValue,
      playerVars: {
        start: this.startingAtValue,
        end: this.endingAtValue,
      },
      videoId: this.codeValue,
      width: this.widthValue,
    })

    player.on("ready", e => {
      this.durationValue = e.target.getDuration()
      this.youtube = e.target
      this.timeValue = this.time
      this.stateValue = -1
      this.readyValue = true
    })

    player.on("stateChange", e => {
      this.stateValue = e.data
      this.timeValue = this.time
      // console.log("State: " + this.stateNames[e.data] + " (" + e.data + ").")
      e.data === 1 ? this.startTimer() : clearInterval(this.timer)
      this.dispatch(this.stateNames[e.data], {
        detail: { event: e, time: this.time },
      })
    })
  }

  disconnect() {
    this.player.destroy()
  }

  startTimer = () => {
    this.timer = setInterval(() => {
      this.timeValue = this.time
      this.dispatch("timer", {
        detail: {
          time: this.time,
        },
      })
    }, 1000)
  }

  play = () => {
    this.player.playVideo()
  }

  pause = () => {
    this.player.pauseVideo()
  }

  stop = () => {
    this.player.stopVideo()
  }

  seek = seconds => {
    this.player.seekTo(seconds)
  }

  get duration() {
    return this.player?.getDuration()
  }

  get loaded() {
    return this.player.getVideoLoadedFraction()
  }

  get player() {
    return this.youtube
  }

  get time() {
    return parseInt(this.player.getCurrentTime())
  }

  get stateNames() {
    return {
      "-1": "unstarted",
      0: "ended",
      1: "playing",
      2: "paused",
      3: "buffering",
      5: "video cued",
    }
  }
}
